import { useMemo, useState } from "react";
import { Card, SectionHeader, TextInput } from "../../components/lib";
import { TabBar, ListRow } from "../../components/lib.overlay";
import { SALES_DEALS, SalesDeal, SalesStage } from "../../mocks/growthData";

const STAGE_META: Record<SalesStage, { bg: string; fg: string; label: string }> = {
  prospect:      { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)",    label: "Prospect"     },
  qualified:     { bg: "var(--ph-ai-tint)",       fg: "var(--ph-ai)",           label: "Qualified"    },
  "sample-sent": { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)",      label: "Sample sent"  },
  quote:         { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)",      label: "Quote"        },
  won:           { bg: "var(--ph-success-tint)", fg: "var(--ph-success)",      label: "Won"          },
  lost:          { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",       label: "Lost"         },
};

export default function SalesPipeline() {
  const [tab, setTab] = useState<SalesStage | "all">("all");
  const [q, setQ] = useState("");
  const filtered = useMemo(() => SALES_DEALS.filter((d) =>
    (tab === "all" || d.stage === tab) &&
    (!q || d.customer.toLowerCase().includes(q.toLowerCase())),
  ), [tab, q]);
  const weighted = SALES_DEALS.filter((d) => d.stage !== "lost").reduce((s, d) => s + d.valueEGP * d.probability, 0);

  return (
    <div className="space-y-6" data-testid="gr-sales">
      <Card testId="gr-s-summary">
        <SectionHeader eyebrow="Pipeline" title="Weighted pipeline" />
        <div className="grid grid-cols-3 gap-4">
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total deals</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{SALES_DEALS.length}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Weighted value</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>EGP {(weighted/1000).toFixed(0)}K</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Won this quarter</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>{SALES_DEALS.filter(d => d.stage === "won").length}</div></div>
        </div>
      </Card>

      <Card testId="gr-s-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search deals by customer…" testId="gr-s-search" />
        <div className="mt-4 overflow-x-auto">
          <TabBar<SalesStage | "all">
            testId="gr-s-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",         label: "All"         },
              { key: "prospect",    label: "Prospect"    },
              { key: "qualified",   label: "Qualified"   },
              { key: "sample-sent", label: "Sample sent" },
              { key: "quote",       label: "Quote"       },
              { key: "won",         label: "Won"         },
              { key: "lost",        label: "Lost"        },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="gr-s-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Deals" title={`${filtered.length} deal${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2">
          {filtered.map((d) => {
            const m = STAGE_META[d.stage];
            return (
              <ListRow key={d.id} testId={`gr-d-${d.id}`}
                label={<span className="inline-flex items-center gap-2">{d.customer}
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{m.label}</span>
                </span>}
                sublabel={<>{d.city} · {d.productInterest.join(", ")} · owner {d.ownerName} · last {d.lastTouchRelative}</>}
                trailing={<div className="text-right"><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {(d.valueEGP/1000).toFixed(0)}K</div><div className="text-[10px] tabular-nums" style={{ color: "var(--ph-ai)" }}>P {Math.round(d.probability*100)}%</div></div>}
              />
            );
          })}
        </div>
      </Card>
    </div>
  );
}
